import { ErrorHandler, makeEnvironmentProviders } from '@angular/core';
import { PCP_TELEMETRY_SINK } from '@pcp/logging';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { ApplicationinsightsAngularpluginErrorService } from '@microsoft/applicationinsights-angularplugin-js';
import { ApplicationInsightsTelemetrySink } from './adapters/application-insights-telemetry-sink';
import { ApplicationInsightsFactoryService } from './services/application-insights-factory.service';
import { PcpInsightsErrorHandler } from './services/pcp-insights-error-handler';
import { PcpApplicationInsights } from './types/application-insights-interface';
import { PCP_INSIGHTS_CONFIG, PcpInsightsConfigInterface } from './injection-tokens';

/**
 * Provides Application Insights integration for the PCP logging system.
 *
 * Supplies `PCP_TELEMETRY_SINK`, the destination `@pcp/logging` sends to. Without it the logger
 * writes to the console and sends nothing.
 * Use alongside `provideLogging()` in app bootstrap:
 *
 * ```typescript
 * bootstrapApplication(AppComponent, {
 *     providers: [
 *         provideLogging({ consoleLevel: LogLevel.DEBUG }),
 *         provideInsights({
 *             connectionString: environment.app_insights_connection_string,
 *             cloudRoleName: 'Teacher-App',
 *             applicationVersion: version.build_date,
 *             logNgrxActions: false,
 *         }),
 *     ],
 * });
 * ```
 */
export function provideInsights(config: PcpInsightsConfigInterface) {
    return makeEnvironmentProviders([
        { provide: PCP_INSIGHTS_CONFIG, useValue: config },
        ApplicationInsightsFactoryService,
        {
            provide: PcpApplicationInsights,
            deps: [ApplicationInsightsFactoryService],
            useFactory: (factory: ApplicationInsightsFactoryService) => factory.create(),
        },
        { provide: ApplicationInsights, useExisting: PcpApplicationInsights },
        { provide: PCP_TELEMETRY_SINK, useClass: ApplicationInsightsTelemetrySink },
        PcpInsightsErrorHandler,
        { provide: ErrorHandler, useExisting: ApplicationinsightsAngularpluginErrorService },
    ]);
}
